export type ColorToken = { light: string; dark: string };

export type ColorShadeTokens = {
  90: ColorToken;
  70: ColorToken;
  50: ColorToken;
  30: ColorToken;
  10: ColorToken;
};

export type GrayShadeTokens = ColorShadeTokens & {
  5: ColorToken;
};

export type GoldShadeTokens = {
  50: ColorToken;
};

export type SurfaceTokens = {
  pure: ColorToken;
  warm: ColorToken;
  cool: ColorToken;
};

export type BrandTokens = {
  illustration: ColorToken;
};

export type ColorTokens = {
  blue: ColorShadeTokens;
  pink: ColorShadeTokens;
  purple: ColorShadeTokens;
  red: ColorShadeTokens;
  green: ColorShadeTokens;
  gold: GoldShadeTokens;
  gray: GrayShadeTokens;
  surface: SurfaceTokens;
  brand: BrandTokens;
};

// Resolved palette type — same shape as ColorTokens but with string values instead of ColorToken
type ResolveTokens<T> = {
  [K in keyof T]: T[K] extends Record<string, ColorToken> ? { [S in keyof T[K]]: string } : never;
};
export type ThemePalette = ResolveTokens<ColorTokens>;

export const tokens: ColorTokens = {
  blue: {
    90: { light: '#012D6A', dark: '#9FCFFF' },
    70: { light: '#014A9B', dark: '#6FB7FF' },
    50: { light: '#0066CC', dark: '#3F9FFF' },
    30: { light: '#629AEA', dark: '#265F99' },
    10: { light: '#DFEDF8', dark: '#0F253B' },
  },
  pink: {
    90: { light: '#821C71', dark: '#FFBEF2' },
    70: { light: '#972728', dark: '#FFA9EE' },
    50: { light: '#AB3192', dark: '#FF94E9' },
    30: { light: '#E164BE', dark: '#99598C' },
    10: { light: '#FAE6F4', dark: '#331E2F' },
  },
  // Login brand overrides: purple scale maps to Vida login brand colors
  purple: {
    90: { light: '#18033C', dark: '#DEB3FF' },
    70: { light: '#5A3ED0', dark: '#D7A3FF' },
    50: { light: '#6B4DE0', dark: '#CF92FF' },
    30: { light: '#9B8BC8', dark: '#7C5899' },
    10: { light: '#F0EBFF', dark: '#291D33' },
  },
  red: {
    90: { light: '#A00000', dark: '#FF9090' },
    70: { light: '#BE0A0C', dark: '#FF6B6B' },
    50: { light: '#f06163', dark: '#FF4545' },
    30: { light: '#ED7779', dark: '#992828' },
    10: { light: '#fdf0ef', dark: '#401010' },
  },
  green: {
    90: { light: '#005222', dark: '#61D992' },
    70: { light: '#086B32', dark: '#38BE72' },
    50: { light: '#10B981', dark: '#19A351' },
    30: { light: '#6BB488', dark: '#127239' },
    10: { light: '#C1E4CF', dark: '#0A4020' },
  },
  gold: {
    50: { light: '#FF9900', dark: '#FF9900' },
  },
  gray: {
    90: { light: '#232323', dark: '#F9F9F9' },
    70: { light: '#4A4A4A', dark: '#EEEEEE' },
    50: { light: '#6B6B6B', dark: '#BDBDBD' },
    30: { light: '#A5A5A5', dark: '#808080' },
    10: { light: '#E5E5E5', dark: '#4B4B4B' },
    5: { light: '#F9F9F9', dark: '#232323' },
  },
  surface: {
    pure: { light: '#FFFFFF', dark: '#000000' },
    warm: { light: '#FDF6EE', dark: '#191510' },
    cool: { light: '#F9FBFE', dark: '#0F151F' },
  },
  // Login-specific brand tokens not in the reactnative repo
  brand: {
    illustration: { light: '#240059', dark: '#240059' },
  },
};

export type Mode = 'light' | 'dark';

export function hexToRgbChannels(hex: string): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

export function buildThemeVars(mode: Mode): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [group, shades] of Object.entries(tokens)) {
    for (const [shade, token] of Object.entries(shades as Record<string, ColorToken>)) {
      // Namespaced under `--color-login-*` to match `login-*` tailwind keys in
      // tailwind-preset.js — prevents any collision with a host app's CSS vars.
      result[`--color-login-${group}-${shade}`] = hexToRgbChannels(token[mode]);
    }
  }

  return result;
}
